import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signInWithPopup, 
  signInWithRedirect, 
  getRedirectResult,
  GoogleAuthProvider,
  sendPasswordResetEmail,
  updatePassword,
  onAuthStateChanged,
  signOut,
  signInWithCredential,
  sendEmailVerification,
  EmailAuthProvider,
  linkWithCredential,
  updateProfile
} from 'firebase/auth';
import { 
  doc, 
  setDoc, 
  getDoc, 
  collection, 
  query, 
  where, 
  getDocs, 
  serverTimestamp,
  updateDoc
} from '../firebase.config';
import { increment } from 'firebase/firestore';
import { Capacitor } from '@capacitor/core';
import { auth, db, googleProvider } from '../firebase.config';
import { AuthService } from '../utils/authService';
import { normalizeEmail, getAuthErrorMessage, isValidEmail } from '../utils/emailUtils';
import { writeSessionExpiry, clearSessionStorageKeys } from '../utils/sessionExpiry';
import {
  startFirebasePhoneAuth,
  getPhoneAuthErrorMessage,
  isFirebasePhoneVerified,
  toE164Phone,
  clearPhoneRecaptcha,
  restorePhoneAuthSession,
  clearPhoneAuthSession,
  normalizeOtpCode,
} from '../utils/firebasePhoneAuth';
import bcrypt from 'bcryptjs';
import { useErrorHandler } from '../hooks/useErrorHandler';
import ErrorHandler from '../utils/errorHandler';
import { Mail, Phone, Lock, Eye, EyeOff, ArrowLeft, CheckCircle2, XCircle, Smartphone, User, LogIn, Sparkles, ArrowRight } from 'lucide-react';

const getSafeInternalPath = (raw) => {
  if (!raw || typeof raw !== 'string') return null;
  let path = raw;
  try {
    path = decodeURIComponent(raw);
  } catch (_) {
    return null;
  }
  if (!path.startsWith('/') || path.startsWith('//') || path.includes('://')) return null;
  if (path === '/login' || path.startsWith('/login?')) return null;
  return path;
};

const NewLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { handleError, handleValidationError, handleFirebaseError, handleNetworkError, showSuccess, showWarning, showInfo } = useErrorHandler();
  
  // State management
  const [currentStep, setCurrentStep] = useState('entry'); // 'entry', 'login', 'signup', 'otp', 'reset'
  const [inputValue, setInputValue] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [userDetails, setUserDetails] = useState({
    name: '',
    email: '',
    phone: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);
  const [isPhoneSignup, setIsPhoneSignup] = useState(false);
  const phoneConfirmRef = useRef(null);
  const [userExists, setUserExists] = useState(null);
  const [isPhoneNumber, setIsPhoneNumber] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(0);
  
  const otpIntervalRef = useRef(null);
  const verificationRedirectTimeoutRef = useRef(null);

  // Utility functions
  const validateEmail = (email) => {
    // Check if it contains @ and . and has letters (not just numbers)
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && /[a-zA-Z]/.test(email);
  };

  const validatePhone = (phone) => {
    // Only consider it a phone if ALL characters are numbers (with optional +91 prefix)
    const cleanPhone = phone.replace(/\D/g, '');
    return /^(\+91|91)?[6-9]\d{9}$/.test(cleanPhone) && /^\d+$/.test(phone.replace(/^\+91/, ''));
  };

  const normalizePhone = (phone) => toE164Phone(phone) || phone;

  const OTP_RATE_LIMIT_MS = 60 * 60 * 1000; // 1 hour after Firebase too-many-requests

  const getOtpRateLimitRemaining = (phoneNumber) => {
    try {
      const key = `phone_otp_rate_limit_${normalizePhone(phoneNumber)}`;
      const until = parseInt(localStorage.getItem(key) || '0', 10);
      if (!until || Date.now() >= until) return 0;
      return Math.ceil((until - Date.now()) / 1000);
    } catch {
      return 0;
    }
  };

  const setOtpRateLimit = (phoneNumber) => {
    try {
      const key = `phone_otp_rate_limit_${normalizePhone(phoneNumber)}`;
      localStorage.setItem(key, String(Date.now() + OTP_RATE_LIMIT_MS));
    } catch (_) {}
  };

  // Send OTP via Firebase Phone Auth (web reCAPTCHA / native Capacitor)
  // Returns true if SMS was started successfully
  const sendOTP = async (phoneNumber) => {
    const rateLimitSec = getOtpRateLimitRemaining(phoneNumber);
    if (rateLimitSec > 0) {
      const msg = `SMS is temporarily blocked for this number. Wait ${Math.ceil(rateLimitSec / 60)} min or sign in with password / email verification.`;
      setError(msg);
      return false;
    }

    try {
      setLoading(true);
      setError('');

      const session = await startFirebasePhoneAuth(phoneNumber, {
        containerId: 'new-login-recaptcha',
      });
      phoneConfirmRef.current = session;

      setOtp('');
      setOtpSent(true);
      setOtpTimer(60);
      setMessage('OTP sent via Firebase. Enter the code from your SMS.');
      showInfo('Check your phone for the verification SMS.', {
        title: 'OTP Sent',
        duration: 5000,
      });

      if (otpIntervalRef.current) clearInterval(otpIntervalRef.current);
      otpIntervalRef.current = setInterval(() => {
        setOtpTimer((prev) => {
          if (prev <= 1) {
            clearInterval(otpIntervalRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return true;
    } catch (error) {
      console.error('Send OTP error:', error);
      clearPhoneRecaptcha();
      clearPhoneAuthSession();
      phoneConfirmRef.current = null;
      const msg = getPhoneAuthErrorMessage(error);
      if (error?.code === 'auth/too-many-requests') {
        setOtpRateLimit(phoneNumber);
      }
      // Avoid duplicate toast spam when user is already on password login
      if (error?.code !== 'auth/too-many-requests') {
        handleError(error, { title: 'OTP Failed', context: 'Send OTP' });
      }
      setError(msg);
      setOtpSent(false);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Confirm Firebase Phone Auth OTP
  const verifyOTP = async (phoneNumber, otpCode) => {
    try {
      setLoading(true);
      setError('');

      let session = phoneConfirmRef.current;
      if (!session?.confirm) {
        session = restorePhoneAuthSession(phoneNumber);
        if (session?.confirm) {
          phoneConfirmRef.current = session;
        }
      }
      if (!session?.confirm) {
        setMessage('');
        setError('Verification session lost. Tap Resend OTP and enter the new code.');
        return { ok: false };
      }

      const result = await session.confirm(normalizeOtpCode(otpCode));
      const phone = result?.phone || normalizePhone(phoneNumber);

      return { ok: true, phone, user: result?.user || auth.currentUser };
    } catch (error) {
      console.error('Verify OTP error:', error);
      const msg = getPhoneAuthErrorMessage(error);
      handleFirebaseError(error, { title: 'OTP Verification Failed', context: 'Verify OTP' });
      setMessage('');
      setError(msg);
      return { ok: false };
    } finally {
      setLoading(false);
    }
  };

  const hashPassword = async (password) => {
    const saltRounds = 12;
    return await bcrypt.hash(password, saltRounds);
  };

  const verifyPassword = async (password, hash) => {
    return await bcrypt.compare(password, hash);
  };

  // Check if user exists in database
  const checkUserExists = async (identifier) => {
    try {
      setLoading(true);
      setError('');
      
      const isEmail = validateEmail(identifier);
      const isPhone = validatePhone(identifier);
      
      if (!isEmail && !isPhone) {
        const validationError = new Error('Please enter a valid email or phone number');
        validationError.code = 'VALIDATION_ERROR';
        handleValidationError(validationError, {
          title: 'Invalid Input',
          context: 'User Check'
        });
        setError('Please enter a valid email or phone number');
        return null;
      }

      // Check network status
      if (!navigator.onLine) {
        const networkError = new Error('No internet connection. Please check your network and try again.');
        networkError.code = 'OFFLINE';
        handleNetworkError(networkError, {
          title: 'No Internet Connection',
          context: 'User Check'
        });
        setError('No internet connection. Please check your network and try again.');
        setLoading(false);
        return null;
      }

      let userQuery;
      if (isEmail) {
        const emailKey = normalizeEmail(identifier);
        userQuery = query(collection(db, 'users'), where('email', '==', emailKey));
      } else {
        const normalizedPhone = normalizePhone(identifier);
        userQuery = query(collection(db, 'users'), where('phone', '==', normalizedPhone));
      }

      // Add timeout handling (8 seconds to avoid Firestore's 10-second timeout)
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Connection timeout. Please check your internet connection and try again.')), 8000);
      });

      const userSnap = await Promise.race([
        getDocs(userQuery),
        timeoutPromise
      ]);
      
      if (!userSnap.empty) {
        const userRecord = { id: userSnap.docs[0].id, ...userSnap.docs[0].data() };
        setUserExists(userRecord);
        setIsPhoneNumber(isPhone);
        return userRecord;
      } else if (isPhone) {
        // If phone not found in users, check phone_index
        const normalizedPhone = normalizePhone(identifier);
        console.log('🔍 Checking phone_index for:', normalizedPhone);
        
        try {
          const phoneIndexDoc = await Promise.race([
            getDoc(doc(db, 'phone_index', normalizedPhone)),
            new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout')), 8000))
          ]);
        
        if (phoneIndexDoc.exists()) {
          console.log('✅ Found in phone_index:', phoneIndexDoc.data());
          const phoneData = phoneIndexDoc.data();
          // Get user data using the UID from phone index
            const userDoc = await Promise.race([
              getDoc(doc(db, 'users', phoneData.uid)),
              new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout')), 8000))
            ]);
          
          if (userDoc.exists()) {
            const userRecord = { id: userDoc.id, ...userDoc.data() };
            console.log('✅ Found user data:', userRecord);
            setUserExists(userRecord);
            setIsPhoneNumber(isPhone);
            return userRecord;
          }
          }
        } catch (phoneIndexError) {
          console.warn('Error checking phone_index:', phoneIndexError);
          // Continue to check 10-digit version
        }
        
        // Also check 10-digit version if it's a +91 number
        if (/^\+91\d{10}$/.test(normalizedPhone)) {
          const tenDigit = normalizedPhone.slice(3);
          console.log('🔍 Checking 10-digit version:', tenDigit);
          
          try {
            const tenDigitDoc = await Promise.race([
              getDoc(doc(db, 'phone_index', tenDigit)),
              new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout')), 8000))
            ]);
          
          if (tenDigitDoc.exists()) {
            console.log('✅ Found in 10-digit phone_index:', tenDigitDoc.data());
            const phoneData = tenDigitDoc.data();
              const userDoc = await Promise.race([
                getDoc(doc(db, 'users', phoneData.uid)),
                new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout')), 8000))
              ]);
            
            if (userDoc.exists()) {
              const userRecord = { id: userDoc.id, ...userDoc.data() };
              console.log('✅ Found user data via 10-digit:', userRecord);
              setUserExists(userRecord);
              setIsPhoneNumber(isPhone);
              return userRecord;
            }
            }
          } catch (tenDigitError) {
            console.warn('Error checking 10-digit phone_index:', tenDigitError);
          }
        }
        
        console.log('❌ Phone not found in phone_index');
      }
      
      setUserExists(null);
      setIsPhoneNumber(isPhone);
      return false;
    } catch (error) {
      console.error('Error checking user:', error);
      
      // Handle specific error types with notifications
      if (error.message && error.message.includes('timeout')) {
        const networkError = new Error('Connection timeout. Please check your internet connection and try again.');
        networkError.code = 'TIMEOUT';
        handleNetworkError(networkError, {
          title: 'Connection Timeout',
          context: 'User Check'
        });
        setError('Connection timeout. Please check your internet connection and try again.');
      } else if (error.message && (error.message.includes('Failed to get document') || error.message.includes('Could not reach'))) {
        const networkError = new Error('Unable to connect to server. Please check your internet connection and try again.');
        networkError.code = 'NETWORK_ERROR';
        handleNetworkError(networkError, {
          title: 'Connection Error',
          context: 'User Check'
        });
        setError('Unable to connect to server. Please check your internet connection and try again.');
      } else if (!navigator.onLine) {
        const networkError = new Error('No internet connection. Please check your network and try again.');
        networkError.code = 'OFFLINE';
        handleNetworkError(networkError, {
          title: 'No Internet Connection',
          context: 'User Check'
        });
        setError('No internet connection. Please check your network and try again.');
      } else {
        handleError(error, {
          title: 'Error Checking User',
          context: 'User Check'
        });
        setError('Error checking user. Please try again.');
      }
      
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Handle entry submission
  const handleEntrySubmit = async (e) => {
    e.preventDefault();
    
    if (!inputValue.trim()) {
      setError('Please enter your email or phone number');
      return;
    }

    // Detect from current input — do not rely on isPhoneNumber state after await
    // (setState from checkUserExists has not flushed yet, so that flag is stale)
    const entryIsPhone = validatePhone(inputValue);
    const entryIsEmail = validateEmail(inputValue);
    
    console.log('🔍 Entry submitted:', inputValue);
    console.log('🔍 Is email:', entryIsEmail);
    console.log('🔍 Is phone:', entryIsPhone);

    if (!entryIsEmail && !entryIsPhone) {
      setError('Please enter a valid email or phone number');
      return;
    }
    
    const userData = await checkUserExists(inputValue);
    
    console.log('🔍 User data found:', userData);
    console.log('🔍 Is phone number:', entryIsPhone);

    // null = lookup error (already shown); false = no account; object = existing user
    if (userData == null) {
      return;
    }
    
    if (userData) {
      // User exists - proceed to login
      console.log('✅ User exists, proceeding to login');
      if (entryIsPhone) {
        // Phone login — if account has email+password, use password (OTP optional).
        // Only require OTP when there is no email to sign in with.
        console.log('📱 Phone login - checking email availability');
        console.log('📧 User email:', userData.email);
        console.log('📧 User email verified:', userData.emailVerified);
        
        if (userData.email) {
          console.log('📧 Account has email — asking for password (OTP available as option)');
          setIsPhoneSignup(false);
          setCurrentStep('login');
        } else {
          console.log('📱 No email on account — sending OTP');
          setIsPhoneSignup(false);
          const sent = await sendOTP(inputValue);
          if (sent) {
            setCurrentStep('otp');
          } else {
            // Stay on entry with the error from sendOTP (rate limit / credential issues)
            setCurrentStep('entry');
          }
        }
      } else {
        // Email login - ask for password
        console.log('📧 Email login - asking for password');
        setCurrentStep('login');
      }
    } else {
      // User doesn't exist - proceed to signup form (same for phone and email).
      // Phone OTP is only for existing-user login; new accounts collect details first.
      console.log('❌ User not found, proceeding to signup');
      if (entryIsPhone) {
        console.log('📱 New phone — opening signup with phone pre-filled');
        setIsPhoneSignup(true);
        setUserDetails((prev) => ({
          ...prev,
          phone: inputValue,
          email: '',
        }));
        setCurrentStep('signup');
      } else {
        console.log('📧 Pre-filling email:', inputValue);
        setIsPhoneSignup(false);
        setUserDetails((prev) => ({
          ...prev,
          email: inputValue,
          phone: '',
        }));
        setCurrentStep('signup');
      }
    }
  };

  const applyIncorrectPassword = async () => {
    if (!userExists?.id) {
      setError('Incorrect password. Please try again.');
      return;
    }

    const newAttempts = (userExists.failedLoginAttempts || 0) + 1;
    try {
      await updateDoc(doc(db, 'users', userExists.id), {
        failedLoginAttempts: newAttempts,
        lastLoginAttempt: serverTimestamp()
      });
    } catch (updateError) {
      console.warn('Could not record failed login attempt:', updateError);
    }

    setFailedAttempts(newAttempts);
    setUserExists((prev) => (prev ? { ...prev, failedLoginAttempts: newAttempts } : prev));

    const remaining = Math.max(0, 3 - newAttempts);
    const message = remaining > 0
      ? `Incorrect password. ${remaining} attempts remaining.`
      : 'Too many failed attempts. Please reset your password or use OTP login.';
    const passwordError = new Error(message);
    passwordError.code = 'AUTH_ERROR';
    handleFirebaseError(passwordError, {
      title: newAttempts >= 3 ? 'Login Failed - Multiple Attempts' : 'Incorrect Password',
      context: 'Login',
      duration: newAttempts >= 3 ? 10000 : 7000
    });
    setError(message);

    if (newAttempts >= 3) {
      setCurrentStep(userExists.email ? 'reset' : 'login');
    }
  };

  // Handle email/password login
  const handleEmailLogin = async (e) => {
    e.preventDefault();
    
    if (!password.trim()) {
      const validationError = new Error('Please enter your password');
      validationError.code = 'VALIDATION_ERROR';
      handleValidationError(validationError, {
        title: 'Password Required',
        context: 'Login'
      });
      setError('Please enter your password');
      return;
    }
    
    // Check if user exists
    if (!userExists || !userExists.email) {
      const validationError = new Error('User information not found. Please check your email/phone and try again.');
      validationError.code = 'VALIDATION_ERROR';
      handleValidationError(validationError, {
        title: 'User Not Found',
        context: 'Login'
      });
      setError('User information not found. Please check your email/phone and try again.');
      setCurrentStep('entry');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      // Check failed attempts (with null safety)
      const currentFailedAttempts = userExists.failedLoginAttempts || 0;
      if (currentFailedAttempts >= 3) {
        const validationError = new Error('Too many failed attempts. Please reset your password or use OTP login.');
        validationError.code = 'VALIDATION_ERROR';
        handleValidationError(validationError, {
          title: 'Too Many Failed Attempts',
          context: 'Login',
          duration: 10000
        });
        setError('Too many failed attempts. Please reset your password or use OTP login.');
        setCurrentStep('reset');
        return;
      }
      
      // Try Firebase Auth first (primary method)
      console.log('🔐 Attempting Firebase Auth login - Email:', userExists.email);
      
      try {
        // Sign in with Firebase Auth using normalized email + password
        const loginEmail = normalizeEmail(userExists.email);
        await signInWithEmailAndPassword(auth, loginEmail, password);
        
        // Reset failed attempts on success
        await updateDoc(doc(db, 'users', userExists.id), {
          failedLoginAttempts: 0,
          lastLoginAt: serverTimestamp()
        });
        
        console.log('✅ Firebase Auth login successful');
        showSuccess('Login successful!', { title: 'Welcome Back' });
        setMessage('Login successful!');
        await handleLoginSuccess(userExists);
        
      } catch (firebaseError) {
        console.error('Firebase Auth error:', firebaseError);
        
        const isCredentialError = [
          'auth/wrong-password',
          'auth/user-not-found',
          'auth/invalid-credential',
          'auth/invalid-login-credentials',
        ].includes(firebaseError.code);

        // Credential errors mean wrong password (or a legacy Firestore-only hash).
        // Never treat a missing Firestore hash as "no password set" — that jumps
        // email users into a broken OTP screen after a simple typo.
        if (isCredentialError) {
          if (userExists.passwordHash) {
            console.log('🔄 Firebase Auth failed, trying password hash fallback');
            const isPasswordValid = await verifyPassword(password, userExists.passwordHash);
            console.log('🔐 Password hash verification:', isPasswordValid);

            if (isPasswordValid) {
              // Password is correct but Firebase Auth failed - create Firebase user
              console.log('🔐 Password correct, creating Firebase Auth user');
              try {
                console.log('🔐 Creating Firebase Auth user for:', userExists.email);
                const firebaseUser = await createUserWithEmailAndPassword(auth, userExists.email, password);
                console.log('✅ Firebase Auth user created:', firebaseUser.user.uid);

                await updateDoc(doc(db, 'users', userExists.id), {
                  firebaseUID: firebaseUser.user.uid,
                  failedLoginAttempts: 0,
                  lastLoginAt: serverTimestamp()
                });

                console.log('✅ User document updated with Firebase UID');
                showSuccess('Login successful! Account linked to Firebase Auth.', { title: 'Account Linked' });
                setMessage('Login successful! Account linked to Firebase Auth.');
                await handleLoginSuccess(userExists);
              } catch (createError) {
                console.error('Error creating Firebase Auth user:', createError);
                if (createError.code === 'auth/email-already-in-use') {
                  // Firebase already has this email — the typed password was wrong.
                  await applyIncorrectPassword();
                } else {
                  handleFirebaseError(createError, {
                    title: 'Account Setup Error',
                    context: 'Login'
                  });
                  setError('Account setup error: ' + createError.message);
                }
              }
            } else {
              await applyIncorrectPassword();
            }
          } else {
            await applyIncorrectPassword();
          }
        } else {
          // Other Firebase Auth errors
          handleFirebaseError(firebaseError, {
            title: 'Login Failed',
            context: 'Login'
          });
          setError(getAuthErrorMessage(firebaseError.code, firebaseError.message || 'Login failed. Please try again.'));
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      handleError(error, {
        title: 'Login Failed',
        context: 'Login'
      });
      setError('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle OTP login / phone-first signup continue
  const handleOTPLogin = async (e) => {
    e.preventDefault();
    
    if (!otp.trim()) {
      const validationError = new Error('Please enter the OTP');
      validationError.code = 'VALIDATION_ERROR';
      handleValidationError(validationError, {
        title: 'OTP Required',
        context: 'OTP Login'
      });
      setError('Please enter the OTP');
      return;
    }

    // Existing-user login requires a Firestore profile; phone-first signup does not
    if (!isPhoneSignup && (!userExists || !userExists.id)) {
      const validationError = new Error('User information not found. Please check your email/phone and try again.');
      validationError.code = 'VALIDATION_ERROR';
      handleValidationError(validationError, {
        title: 'User Not Found',
        context: 'OTP Login'
      });
      setError('User information not found. Please check your email/phone and try again.');
      setCurrentStep('entry');
      return;
    }
    
    const otpResult = await verifyOTP(inputValue, otp);
    
    if (otpResult?.ok) {
      setMessage('Phone verified!');

      const authUser = auth.currentUser;
      const normalized = otpResult.phone || normalizePhone(inputValue);

      try {
        if (authUser?.uid) {
          await setDoc(
            doc(db, 'phone_index', normalized),
            {
              uid: authUser.uid,
              email: userExists?.email || authUser.email || '',
              updatedAt: serverTimestamp(),
            },
            { merge: true }
          );
          if (/^\+91\d{10}$/.test(normalized)) {
            await setDoc(
              doc(db, 'phone_index', normalized.slice(3)),
              {
                uid: authUser.uid,
                email: userExists?.email || authUser.email || '',
                updatedAt: serverTimestamp(),
              },
              { merge: true }
            );
          }
        }
      } catch (indexErr) {
        console.warn('Could not update phone_index after OTP:', indexErr);
      }

      if (isPhoneSignup) {
        setUserDetails((prev) => ({
          ...prev,
          phone: inputValue,
          email: '',
        }));
        setCurrentStep('signup');
        setMessage('Phone verified. Complete your account details.');
        phoneConfirmRef.current = null;
        return;
      }

      try {
        if (authUser?.uid && userExists.id) {
          await updateDoc(doc(db, 'users', userExists.id), {
            lastLoginAt: serverTimestamp(),
            firebaseUID: authUser.uid,
            phone: normalized,
            phoneVerified: true,
          });
        }
      } catch (linkErr) {
        console.warn('Could not link Firebase UID after OTP:', linkErr);
      }
      
      await handleLoginSuccess({
        ...userExists,
        firebaseUID: authUser?.uid || userExists.firebaseUID || userExists.id,
        phone: normalized,
        phoneVerified: true,
        authProvider: 'phone',
      });
      phoneConfirmRef.current = null;
    }
  };

  // Handle Google login
  const handleGoogleLogin = async () => {
    try {
      setLoading(true);
      setError('');
      
      console.log('🚀 GOOGLE SIGN-IN STARTED');
      
      // Platform detection using Capacitor API
      const isNativePlatform = Capacitor.isNativePlatform();
      
      console.log('🔍 PLATFORM DETECTION:');
      console.log('  - isNativePlatform:', isNativePlatform);
      console.log('  - Platform:', Capacitor.getPlatform());
      
      let result;
      
      if (isNativePlatform) {
        // NATIVE PLATFORM (Android/iOS) - Use Capacitor Firebase Plugin
        console.log('📱 NATIVE OAUTH FLOW - Using @capacitor-firebase/authentication');
        
        try {
          // Step 1: Import native plugin
          console.log('🔧 Step 1: Importing native plugin...');
          const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
          console.log('✅ Plugin imported');
          
          // Step 2: Verify plugin is available
          if (!FirebaseAuthentication || !FirebaseAuthentication.signInWithGoogle) {
            throw new Error('Native plugin not available');
          }
          console.log('✅ Plugin available');
          
          // Step 3: Call native Google Sign-In
          console.log('🔧 Step 3: Calling native signInWithGoogle()...');
          const nativeResult = await FirebaseAuthentication.signInWithGoogle();
          
          console.log('✅ Native sign-in returned!');
          console.log('  - User:', nativeResult.user?.email);
          console.log('  - Has ID token:', !!nativeResult.credential?.idToken);
          
          if (!nativeResult.credential?.idToken) {
            throw new Error('No ID token received');
          }
          
          // Step 4: Create Firebase credential
          console.log('🔧 Step 4: Creating Firebase credential...');
          const credential = GoogleAuthProvider.credential(nativeResult.credential.idToken);
          
          // Step 5: Sign in to Firebase
          console.log('🔧 Step 5: Signing in to Firebase...');
          result = await signInWithCredential(auth, credential);
          
          console.log('✅ Firebase sign-in SUCCESSFUL!');
          console.log('  - User email:', result.user.email);
          
        } catch (nativeError) {
          console.error('❌ Native OAuth failed:', nativeError);
          
          // Check for specific error types
          if (nativeError.message?.includes('No credentials available')) {
            console.log('⚠️ No Google account found on device');
            throw new Error('No Google account found on this device. Please add a Google account in Settings > Accounts, or use email/password login.');
          } else if (nativeError.message?.includes('not available')) {
            throw new Error('Google Sign-In is not available on this device. Please use email/password login.');
          } else {
            throw nativeError;
          }
        }
        
      } else {
        // WEB PLATFORM (Browser) - Use Firebase Web SDK
        console.log('🌐 WEB OAUTH FLOW - Using signInWithPopup');
        result = await signInWithPopup(auth, googleProvider);
        console.log('✅ Web OAuth successful');
      }
      
      // Check if user exists in our database
      const googleEmail = normalizeEmail(result.user.email);
      const userQuery = query(collection(db, 'users'), where('email', '==', googleEmail));
      const userSnap = await getDocs(userQuery);
      
      if (userSnap.empty) {
        // New user - create account
        const hashedPassword = await hashPassword('google_' + result.user.uid);
        
        const newUser = {
          email: googleEmail,
          name: result.user.displayName,
          phone: result.user.phoneNumber || '',
          passwordHash: hashedPassword,
          authProvider: 'google',
          firebaseUID: result.user.uid,
          createdAt: serverTimestamp(),
          lastLoginAt: serverTimestamp(),
          failedLoginAttempts: 0
        };
        
        await setDoc(doc(db, 'users', result.user.uid), newUser);
        
        // Update phone index if phone number exists
        if (result.user.phoneNumber) {
          try {
            const normalizedPhone = normalizePhone(result.user.phoneNumber);
            await setDoc(doc(db, 'phone_index', normalizedPhone), {
              uid: result.user.uid,
              email: googleEmail,
              updatedAt: serverTimestamp()
            }, { merge: true });
          } catch (phoneIndexError) {
            console.log('Could not update phone index for Google user:', phoneIndexError);
          }
        }
        
        showSuccess('Account created successfully!', { title: 'Welcome' });
        setMessage('Account created successfully!');
        await handleLoginSuccess({ ...newUser, id: result.user.uid, firebaseUID: result.user.uid });
      } else {
        // Existing user
        const userDoc = userSnap.docs[0];
        const userData = userDoc.data();
        await updateDoc(doc(db, 'users', userDoc.id), {
          lastLoginAt: serverTimestamp()
        });
        showSuccess('Login successful!', { title: 'Welcome Back' });
        setMessage('Login successful!');
        await handleLoginSuccess({
          ...userData,
          id: userDoc.id,
          firebaseUID: userData.firebaseUID || result.user.uid,
          email: userData.email || googleEmail,
        });
      }
    } catch (error) {
      console.error('Google login error:', error);
      const code = error?.code || '';
      const message = error?.message || '';

      if (
        code === 'auth/popup-closed-by-user' ||
        code === 'auth/cancelled-popup-request' ||
        message.includes('12501') ||
        message.toLowerCase().includes('cancel')
      ) {
        setError('Google Sign-In was cancelled.');
        showInfo('Sign-in cancelled.', { title: 'Google Sign-In', duration: 3000 });
      } else if (code === 'auth/account-exists-with-different-credential') {
        setError('An account already exists with this email using a different sign-in method. Try email/password or reset your password.');
        handleFirebaseError(error, { title: 'Account Exists', context: 'Google Login' });
      } else if (
        code === 'auth/invalid-credential' ||
        message.includes('DEVELOPER_ERROR') ||
        message.includes('10:')
      ) {
        setError('Google Sign-In configuration error. Please try again or use email/password.');
        handleFirebaseError(error, { title: 'Google Login Failed', context: 'Authentication' });
      } else if (message?.includes('No Google account found')) {
        handleValidationError(Object.assign(new Error(message), { code: 'VALIDATION_ERROR' }), {
          title: 'Google Account Not Found',
          context: 'Google Login',
        });
        setError(message);
      } else if (code?.startsWith('auth/')) {
        handleFirebaseError(error, { title: 'Google Login Failed', context: 'Authentication' });
        setError(getAuthErrorMessage(code, 'Google login failed. Please try again.'));
      } else if (message?.includes('Native plugin not available') && !Capacitor.isNativePlatform()) {
        setError('Google Sign-In is unavailable. Please try again.');
      } else {
        handleError(error, { title: 'Google Login Failed', context: 'Authentication' });
        setError('Google login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle signup
  const handleSignup = async (e) => {
    e.preventDefault();
    
    if (!userDetails.name.trim() || !userDetails.email.trim() || !userDetails.phone.trim() || !userDetails.password.trim()) {
      const validationError = new Error('Please fill in all required fields');
      validationError.code = 'VALIDATION_ERROR';
      handleValidationError(validationError, {
        title: 'Missing Required Fields',
        context: 'Signup'
      });
      setError('Please fill in all required fields');
      return;
    }
    
    // Validate phone number
    if (!validatePhone(userDetails.phone)) {
      const validationError = new Error('Please enter a valid phone number');
      validationError.code = 'VALIDATION_ERROR';
      validationError.fieldName = 'phone';
      handleValidationError(validationError, {
        title: 'Invalid Phone Number',
        context: 'Signup'
      });
      setError('Please enter a valid phone number');
      return;
    }
    
    // Validate email
    if (!validateEmail(userDetails.email)) {
      const validationError = new Error('Please enter a valid email address');
      validationError.code = 'VALIDATION_ERROR';
      validationError.fieldName = 'email';
      handleValidationError(validationError, {
        title: 'Invalid Email Address',
        context: 'Signup'
      });
      setError('Please enter a valid email address');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      // Hash password
      const hashedPassword = await hashPassword(userDetails.password);
      const signupEmail = normalizeEmail(userDetails.email);
      const normalizedPhone = normalizePhone(userDetails.phone);

      let authUser = auth.currentUser;
      const alreadyPhoneAuthed = isFirebasePhoneVerified(authUser);

      if (alreadyPhoneAuthed && authUser) {
        // Phone already verified via Firebase Phone Auth — link email/password to same UID
        try {
          await updateProfile(authUser, { displayName: userDetails.name });
        } catch (_) {}
        const emailCred = EmailAuthProvider.credential(signupEmail, userDetails.password);
        try {
          await linkWithCredential(authUser, emailCred);
        } catch (linkErr) {
          if (linkErr?.code === 'auth/email-already-in-use' || linkErr?.code === 'auth/credential-already-in-use') {
            setError('That email is already registered. Sign in with email/password, then link phone in Login & Security.');
            return;
          }
          throw linkErr;
        }
        authUser = auth.currentUser;
      } else {
        // Standard email/password signup
        const firebaseUser = await createUserWithEmailAndPassword(
          auth,
          signupEmail,
          userDetails.password
        );
        authUser = firebaseUser.user;
        try {
          await updateProfile(authUser, { displayName: userDetails.name });
        } catch (_) {}
      }

      const uid = authUser.uid;
      
      // Create user document in Firestore
      const newUser = {
        email: signupEmail,
        phone: normalizedPhone,
        name: userDetails.name,
        passwordHash: hashedPassword,
        authProvider: alreadyPhoneAuthed ? 'phone+email' : 'email',
        firebaseUID: uid,
        phoneVerified: alreadyPhoneAuthed || isFirebasePhoneVerified(authUser),
        createdAt: serverTimestamp(),
        lastLoginAt: serverTimestamp(),
        failedLoginAttempts: 0
      };
      
      await setDoc(doc(db, 'users', uid), newUser);
      
      // Update phone index for easy login lookup
      try {
        await setDoc(doc(db, 'phone_index', normalizedPhone), {
          uid,
          email: signupEmail,
          updatedAt: serverTimestamp()
        }, { merge: true });
        
        if (/^\+91\d{10}$/.test(normalizedPhone)) {
          const tenDigit = normalizedPhone.slice(3);
          await setDoc(doc(db, 'phone_index', tenDigit), {
            uid,
            email: signupEmail,
            updatedAt: serverTimestamp()
          }, { merge: true });
        }
      } catch (phoneIndexError) {
        console.log('Could not update phone index:', phoneIndexError);
      }
      
      await authUser.reload();
      const isEmailVerified = authUser.emailVerified || false;
      const hasGoogleProvider = authUser.providerData?.some(p => p.providerId === 'google.com') || false;
      const hasPhoneAuth = isFirebasePhoneVerified(authUser);
      const isVerified = isEmailVerified || hasGoogleProvider || hasPhoneAuth;
      
      console.log('🔍 Verification check after signup:', {
        emailVerified: isEmailVerified,
        hasGoogle: hasGoogleProvider,
        hasPhoneAuth,
        isVerified,
      });
      
      if (userDetails.email && authUser && isVerified) {
        console.log('✅ User already verified, skipping verification email');
      } else if (userDetails.email && authUser && !isVerified) {
        console.log('📧 User needs verification - email will be sent when requested on VerificationRequired page');
        sessionStorage.setItem(`verification_email_not_sent_${uid}`, 'true');
      }
      
      // Phone Auth OR Google OR email-verified skips the email gate
      if (!isVerified && userDetails.email) {
        showSuccess('Account created! Please verify your email on the next page.', {
          title: 'Welcome - Verification Required',
          duration: 8000
        });
        await handleLoginSuccess({ ...newUser, id: uid, firebaseUID: uid });
      } else {
        if (isVerified) {
          showSuccess('Account created successfully!', { title: 'Welcome' });
          setMessage('Account created successfully!');
        } else {
          showSuccess('Account created! You can verify your email later.', { title: 'Welcome' });
          setMessage('Account created successfully!');
        }
        await handleLoginSuccess({ ...newUser, id: uid, firebaseUID: uid });
      }
      
    } catch (error) {
      console.error('Signup error:', error);
      // Handle password validation errors
      if (error.code === 'auth/weak-password' || error.message?.includes('password-does-not-meet-requirements')) {
        const passwordError = new Error(ErrorHandler.formatPasswordRequirementsError(error.message));
        passwordError.code = 'VALIDATION_ERROR';
        passwordError.fieldName = 'password';
        handleValidationError(passwordError, {
          title: 'Password Requirements',
          context: 'Signup'
        });
      } else if (error.code === 'auth/email-already-in-use') {
        handleFirebaseError(error, {
          title: 'Email Already Exists',
          context: 'Signup'
        });
        const existingEmail = normalizeEmail(userDetails.email);
        const existing = await checkUserExists(existingEmail);
        setError('Email already exists. Please sign in with your password.');
        setInputValue(existingEmail);
        setPassword('');
        setCurrentStep(existing ? 'login' : 'entry');
      } else if (error.code?.startsWith('auth/')) {
        handleFirebaseError(error, {
          title: 'Signup Failed',
          context: 'Signup'
        });
        setError('Signup failed. Please try again.');
      } else {
        handleError(error, {
          title: 'Signup Failed',
          context: 'Signup'
        });
        setError('Signup failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle password reset
  const handlePasswordReset = async () => {
    try {
      setLoading(true);
      setError('');

      // Prefer typed input or known user email; always normalize
      const rawEmail = userExists?.email || (validateEmail(inputValue) ? inputValue : '');
      const email = normalizeEmail(rawEmail);
      if (!email || !isValidEmail(email)) {
        setError('This account has no email on file. Use OTP login, or enter your email on the first screen.');
        setCurrentStep(validatePhone(inputValue) && userExists ? 'login' : 'entry');
        return;
      }

      const actionCodeSettings = {
        url: `${window.location.origin}/login`,
        handleCodeInApp: false,
      };

      const okMsg = 'If an account exists for that email, a password reset link has been sent.';
      try {
        await sendPasswordResetEmail(auth, email, actionCodeSettings);
      } catch (sendError) {
        // Do not reveal whether the email exists
        if (sendError?.code !== 'auth/user-not-found' && sendError?.code !== 'auth/invalid-email') {
          throw sendError;
        }
      }
      showSuccess(okMsg, { title: 'Check Your Email' });
      setMessage(okMsg);
      
    } catch (error) {
      console.error('Password reset error:', error);
      if (error.code?.startsWith('auth/')) {
        handleFirebaseError(error, {
          title: 'Password Reset Failed',
          context: 'Password Reset'
        });
        setError(getAuthErrorMessage(error.code, 'Could not send reset link. Please try again.'));
      } else {
        handleError(error, {
          title: 'Password Reset Failed',
          context: 'Password Reset'
        });
        setError('Failed to send reset link. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle successful login
  const handleLoginSuccess = async (userData) => {
    try {
      // Check if user's email is verified (from Firebase Auth if available)
      let isEmailVerified = false;
      let hasPhoneAuth = !!userData.phoneVerified;
      let hasGoogle = false;
      if (auth.currentUser) {
        await auth.currentUser.reload(); // Reload to get latest verification status
        isEmailVerified = auth.currentUser.emailVerified || false;
        hasPhoneAuth = hasPhoneAuth || isFirebasePhoneVerified(auth.currentUser);
        hasGoogle = auth.currentUser.providerData?.some((p) => p.providerId === 'google.com') || false;
      } else if (userData.emailVerified !== undefined) {
        isEmailVerified = userData.emailVerified;
      }

      const isVerified = !!(isEmailVerified || hasPhoneAuth || hasGoogle);
      
      if (isVerified && hasPhoneAuth && !userData.email && !auth.currentUser?.email) {
        try {
          sessionStorage.setItem('acctoo_prompt_link_email', '1');
        } catch (_) {}
      }
      
      // If user has email but no verification method yet, redirect to verification page
      if (userData.email && !isVerified) {
        console.log('📧 User email not verified, redirecting to verification page...');
        // Save minimal session data for verification page
        const sessionData = {
          userId: userData.firebaseUID || userData.id,
          email: userData.email,
          name: userData.name,
          loginTime: new Date().toISOString(),
          authProvider: userData.authProvider || 'email',
          phoneNumber: auth.currentUser?.phoneNumber || userData.phone || '',
          emailVerified: isEmailVerified,
          isVerified: false,
        };
        localStorage.setItem('acctoo_user', JSON.stringify(sessionData));
        writeSessionExpiry();
        
        // Clear form data
        setInputValue('');
        setPassword('');
        setOtp('');
        setIsPhoneSignup(false);
        setUserDetails({ name: '', email: '', phone: '', password: '' });
        setCurrentStep('entry');
        setMessage('');
        setError('');
        
        // Redirect to verification page
        navigate('/verification-required', { replace: true });
        return;
      }
      
      // Save user session
      const sessionData = {
        userId: userData.firebaseUID || userData.id,
        email: userData.email,
        name: userData.name,
        loginTime: new Date().toISOString(),
        authProvider: userData.authProvider || (hasPhoneAuth ? 'phone' : 'email'),
        phoneNumber: auth.currentUser?.phoneNumber || userData.phone || '',
        emailVerified: isEmailVerified,
        isVerified: true,
      };
      
      localStorage.setItem('acctoo_user', JSON.stringify(sessionData));
      writeSessionExpiry(); // 30 days
      
      // Clear form data
      setInputValue('');
      setPassword('');
      setOtp('');
      setUserDetails({ name: '', email: '', phone: '', password: '' });
      setCurrentStep('entry');
      setMessage('');
      setError('');
      
      // Handle redirect after login
      const params = new URLSearchParams(location.search);
      const redirectPath = params.get('redirect');
      
      // Navigate to the intended path or dashboard
      if (redirectPath && redirectPath !== '/login') {
        const safeRedirect = getSafeInternalPath(redirectPath);
        console.log('🔄 Redirecting to intended path:', safeRedirect || '/dashboard');
        navigate(safeRedirect || '/dashboard', { replace: true });
      } else {
        console.log('🔄 Redirecting to dashboard');
        navigate('/dashboard', { replace: true });
      }
      
    } catch (error) {
      console.error('Error handling login success:', error);
      handleError(error, {
        title: 'Session Setup Failed',
        context: 'Login Success',
        severity: 'warning'
      });
      setError('Login successful but session setup failed. Please refresh the page.');
    }
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      await signOut(auth);
      clearSessionStorageKeys();
      setCurrentStep('entry');
      setMessage('');
      setError('');
    } catch (error) {
      console.error('Logout error:', error);
      handleError(error, {
        title: 'Logout Failed',
        context: 'Logout'
      });
    }
  };

  // Check authentication state on mount
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        const sessionData = JSON.parse(localStorage.getItem('acctoo_user') || '{}');
        if (sessionData.userId) {
          const authVerified = !!(
            user.emailVerified ||
            user.phoneNumber ||
            user.providerData?.some((p) => p.providerId === 'google.com' || p.providerId === 'phone')
          );
          const sessionVerified = !!(sessionData.isVerified || sessionData.emailVerified);
          if (!authVerified && !sessionVerified && user.email) {
            navigate('/verification-required', { replace: true });
            return;
          }

          const params = new URLSearchParams(location.search);
          const redirectPath = getSafeInternalPath(params.get('redirect'));
          navigate(redirectPath || '/dashboard', { replace: true });
        }
      }
    });

    return () => unsubscribe();
  }, [navigate, location.search]);

  // Restore native phone verification session on OTP screen (WebView reload / memory pressure)
  useEffect(() => {
    if (currentStep !== 'otp' || phoneConfirmRef.current?.confirm) return;
    const restored = restorePhoneAuthSession(inputValue);
    if (restored?.confirm) {
      phoneConfirmRef.current = restored;
      setOtpSent(true);
    }
  }, [currentStep, inputValue]);

  // Cleanup OTP timer and verification redirect timeout
  useEffect(() => {
    return () => {
      if (otpIntervalRef.current) {
        clearInterval(otpIntervalRef.current);
      }
      if (verificationRedirectTimeoutRef.current) {
        clearTimeout(verificationRedirectTimeoutRef.current);
      }
      clearPhoneRecaptcha();
      phoneConfirmRef.current = null;
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center p-2 sm:p-4 safe-area-inset">
      {/* Invisible reCAPTCHA host for Firebase Phone Auth (web) */}
      <div id="new-login-recaptcha" />
      {/* Background decoration - Reduced on mobile for performance */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-64 h-64 sm:w-96 sm:h-96 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-10 sm:opacity-20 animate-blob"></div>
        <div className="absolute top-0 left-0 w-64 h-64 sm:w-96 sm:h-96 bg-purple-200 rounded-full mix-blend-multiply filter blur-3xl opacity-10 sm:opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute bottom-0 right-0 w-64 h-64 sm:w-96 sm:h-96 bg-indigo-200 rounded-full mix-blend-multiply filter blur-3xl opacity-10 sm:opacity-20 animate-blob animation-delay-4000"></div>
      </div>

      <div className="relative bg-white rounded-xl sm:rounded-2xl shadow-xl sm:shadow-2xl w-full max-w-md overflow-hidden border border-gray-100 mx-auto">
        {/* Gradient Header - Responsive padding */}
        <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 p-4 sm:p-6 text-white">
          <div className="flex items-center justify-center gap-2 sm:gap-3 mb-1 sm:mb-2">
            <div className="p-1.5 sm:p-2 bg-white bg-opacity-20 rounded-lg sm:rounded-xl backdrop-blur-sm">
              <Sparkles className="w-5 h-5 sm:w-6 sm:h-6" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold">ACCTOO</h1>
          </div>
          <p className="text-center text-blue-100 text-xs sm:text-sm px-2">
            {currentStep === 'entry' && 'Enter your email or phone number'}
            {currentStep === 'login' && 'Enter your password'}
            {currentStep === 'signup' && 'Create your account'}
            {currentStep === 'otp' && 'Enter the OTP sent to your phone'}
            {currentStep === 'reset' && 'Reset your password'}
          </p>
        </div>

        <div className="p-4 sm:p-6">
          
          {/* Error/Success Messages - Responsive */}
          {error && (
            <div className="mb-3 sm:mb-4 bg-gradient-to-r from-red-50 to-rose-50 border-2 border-red-200 text-red-700 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg sm:rounded-xl flex items-start gap-2 text-xs sm:text-sm">
              <XCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <span className="font-medium break-words">{error}</span>
            </div>
          )}
          
          {message && (
            <div className={`mb-3 sm:mb-4 border-2 rounded-lg sm:rounded-xl px-3 sm:px-4 py-2.5 sm:py-3 flex items-start gap-2 text-xs sm:text-sm ${
              message.includes('verify') || message.includes('Verification') || message.includes('verification')
                ? 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-300 text-blue-800 shadow-md' // More prominent for verification messages
                : 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200 text-green-700'
            }`}>
              <CheckCircle2 className={`w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 mt-0.5 ${
                message.includes('verify') || message.includes('Verification') || message.includes('verification')
                  ? 'text-blue-600'
                  : 'text-green-600'
              }`} />
              <span className="font-medium break-words">{message}</span>
            </div>
          )}

          {/* Step 1: Entry (Email/Phone) */}
          {currentStep === 'entry' && (
            <form onSubmit={handleEntrySubmit} className="space-y-4 sm:space-y-5">
              <div className="space-y-2">
                <label htmlFor="identifier" className="block text-xs sm:text-sm font-semibold text-gray-700 flex items-center gap-1.5 sm:gap-2">
                  {validatePhone(inputValue) ? <Phone className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-600" /> : <Mail className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-600" />}
                  Email or Phone Number
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 sm:pl-4 flex items-center pointer-events-none">
                    {validatePhone(inputValue) ? (
                      <Phone className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                    ) : (
                      <Mail className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                    )}
                  </div>
                  <input
                    id="identifier"
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    placeholder="Enter email or phone number"
                    className="block w-full pl-10 sm:pl-12 pr-4 py-3 sm:py-3.5 border-2 border-gray-200 rounded-lg sm:rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50 focus:bg-white text-sm sm:text-base touch-manipulation"
                    required
                    autoComplete="username"
                    inputMode="email"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white py-3.5 sm:py-4 px-4 rounded-lg sm:rounded-xl hover:from-blue-700 hover:via-indigo-700 hover:to-purple-700 active:scale-95 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 shadow-lg hover:shadow-xl transition-all duration-200 sm:hover:scale-[1.02] flex items-center justify-center gap-2 text-sm sm:text-base font-semibold min-h-[44px] touch-manipulation"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Checking...</span>
                  </>
                ) : (
                  <>
                    <span>Continue</span>
                    <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
                  </>
                )}
              </button>

              {/* Google Login Button */}
              <div className="mt-4 sm:mt-6">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200" />
                  </div>
                  <div className="relative flex justify-center text-xs sm:text-sm">
                    <span className="px-3 sm:px-4 bg-white text-gray-500 font-medium">Or continue with</span>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleGoogleLogin}
                  disabled={loading}
                  className="mt-3 sm:mt-4 w-full inline-flex justify-center items-center gap-2 sm:gap-3 py-3 sm:py-3.5 px-4 border-2 border-gray-200 rounded-lg sm:rounded-xl shadow-sm bg-white text-sm sm:text-base font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-300 active:scale-95 disabled:opacity-50 transition-all duration-200 sm:hover:scale-[1.02] min-h-[44px] touch-manipulation"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  <span>Continue with Google</span>
                </button>
              </div>
            </form>
          )}

          {/* Step 2: Login (Password) */}
          {currentStep === 'login' && (
            <form onSubmit={handleEmailLogin} className="space-y-4 sm:space-y-5">
              <div className="text-center mb-3 sm:mb-4">
                <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-xl sm:rounded-2xl mb-2 sm:mb-3">
                  <Lock className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600" />
                </div>
                <h2 className="text-lg sm:text-xl font-bold text-gray-900">Enter your password</h2>
                <p className="text-xs sm:text-sm text-gray-600 mt-1 sm:mt-2 flex items-center justify-center gap-1.5 sm:gap-2">
                  <Mail className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  <span className="break-all">{validatePhone(inputValue) ? `+91-${inputValue}` : inputValue}</span>
                </p>
                {validatePhone(inputValue) && userExists?.email && (
                  <p className="text-xs text-blue-700 mt-2 px-2">
                    This phone is linked to <span className="font-semibold break-all">{userExists.email}</span>.
                    Sign in with that account&apos;s password. SMS login is optional.
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <label htmlFor="password" className="block text-xs sm:text-sm font-semibold text-gray-700 flex items-center gap-1.5 sm:gap-2">
                  <Lock className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-600" />
                  Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 sm:pl-4 flex items-center pointer-events-none">
                    <Lock className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                  </div>
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="block w-full pl-10 sm:pl-12 pr-4 py-3 sm:py-3.5 border-2 border-gray-200 rounded-lg sm:rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50 focus:bg-white text-sm sm:text-base touch-manipulation"
                    required
                    autoComplete="current-password"
                  />
                </div>
                {failedAttempts > 0 && (
                  <p className="mt-1 text-xs sm:text-sm text-red-600 font-medium flex items-center gap-1">
                    <XCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    {3 - failedAttempts} attempts remaining
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white py-3.5 sm:py-4 px-4 rounded-lg sm:rounded-xl hover:from-blue-700 hover:via-indigo-700 hover:to-purple-700 active:scale-95 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 shadow-lg hover:shadow-xl transition-all duration-200 sm:hover:scale-[1.02] flex items-center justify-center gap-2 text-sm sm:text-base font-semibold min-h-[44px] touch-manipulation"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Signing in...</span>
                  </>
                ) : (
                  <>
                    <LogIn className="w-4 h-4 sm:w-5 sm:h-5" />
                    <span>Sign In</span>
                  </>
                )}
              </button>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2 sm:gap-0 pt-2">
                <button
                  type="button"
                  onClick={() => setCurrentStep('reset')}
                  className="text-xs sm:text-sm text-blue-600 hover:text-blue-700 active:text-blue-800 font-medium transition-colors py-2 sm:py-0 touch-manipulation min-h-[44px] sm:min-h-0"
                >
                  Forgot password?
                </button>

                {validatePhone(inputValue) && (
                  <button
                    type="button"
                    disabled={loading || getOtpRateLimitRemaining(inputValue) > 0}
                    onClick={async () => {
                      setIsPhoneSignup(false);
                      setError('');
                      setMessage('');
                      const sent = await sendOTP(inputValue);
                      if (sent) setCurrentStep('otp');
                    }}
                    className="text-xs sm:text-sm text-indigo-600 hover:text-indigo-700 font-medium transition-colors py-2 sm:py-0 touch-manipulation min-h-[44px] sm:min-h-0 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {getOtpRateLimitRemaining(inputValue) > 0
                      ? `SMS blocked (${Math.ceil(getOtpRateLimitRemaining(inputValue) / 60)}m)`
                      : 'Login with OTP'}
                  </button>
                )}
                
                <button
                  type="button"
                  onClick={() => {
                    setCurrentStep('entry');
                    setMessage('');
                    setError('');
                    setPassword('');
                    setFailedAttempts(0);
                  }}
                  className="inline-flex items-center justify-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-gray-600 hover:text-gray-700 active:text-gray-800 font-medium transition-colors py-2 sm:py-0 touch-manipulation min-h-[44px] sm:min-h-0"
                >
                  <ArrowLeft className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  Back
                </button>
              </div>
            </form>
          )}

          {/* Step 3: OTP Verification */}
          {currentStep === 'otp' && (
            <div>
              <form onSubmit={handleOTPLogin} className="space-y-4 sm:space-y-5">
              <div className="text-center mb-3 sm:mb-4">
                <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-xl sm:rounded-2xl mb-2 sm:mb-3">
                  <Smartphone className="w-6 h-6 sm:w-8 sm:h-8 text-indigo-600" />
                </div>
                <h2 className="text-lg sm:text-xl font-bold text-gray-900">Verify your phone</h2>
                <p className="text-xs sm:text-sm text-gray-600 mt-1 sm:mt-2 flex items-center justify-center gap-1.5 sm:gap-2">
                  <Phone className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  <span className="break-all">{validatePhone(inputValue) ? `+91-${inputValue}` : inputValue}</span>
                </p>
                {userExists?.email && (
                  <p className="mt-1 text-xs text-gray-500 break-all px-2">
                    Associated Email: {userExists.email}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <label htmlFor="otp" className="block text-xs sm:text-sm font-semibold text-gray-700 text-center">
                  Enter 6-digit code
                </label>
                <input
                  id="otp"
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  maxLength="6"
                  className="block w-full px-3 sm:px-4 py-3 sm:py-4 border-2 border-gray-200 rounded-lg sm:rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-center text-2xl sm:text-3xl tracking-[0.3em] sm:tracking-[0.5em] font-semibold bg-gray-50 focus:bg-white transition-all touch-manipulation"
                  required
                  autoComplete="one-time-code"
                  inputMode="numeric"
                />
                {otpTimer > 0 && (
                  <p className="mt-1 text-xs sm:text-sm text-gray-600 text-center">
                    Resend OTP in {Math.floor(otpTimer / 60)}:{(otpTimer % 60).toString().padStart(2, '0')}
                  </p>
                )}
                <p className="mt-2 text-[11px] sm:text-xs text-gray-500 text-center px-2">
                  Use the code from your latest SMS. If verification fails, tap Resend OTP and enter the new code.
                </p>
              </div>

              <button
                type="submit"
                disabled={loading || otp.length !== 6}
                className="w-full bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white py-3.5 sm:py-4 px-4 rounded-lg sm:rounded-xl hover:from-indigo-700 hover:via-purple-700 hover:to-pink-700 active:scale-95 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 shadow-lg hover:shadow-xl transition-all duration-200 sm:hover:scale-[1.02] flex items-center justify-center gap-2 text-sm sm:text-base font-semibold min-h-[44px] touch-manipulation"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Verifying...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5" />
                    <span>Verify & Continue</span>
                  </>
                )}
              </button>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2 sm:gap-0 pt-2">
                <button
                  type="button"
                  onClick={async () => {
                    const sent = await sendOTP(inputValue);
                    if (!sent && userExists?.email) {
                      setMessage('SMS unavailable. Sign in with your password instead.');
                      setCurrentStep('login');
                    }
                  }}
                  disabled={otpTimer > 0 || loading}
                  className="text-xs sm:text-sm text-green-600 hover:text-green-700 active:text-green-800 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed py-2 sm:py-0 touch-manipulation min-h-[44px] sm:min-h-0"
                >
                  {otpTimer > 0 ? `Resend in ${Math.floor(otpTimer / 60)}:${(otpTimer % 60).toString().padStart(2, '0')}` : 'Resend OTP'}
                </button>
                
                <button
                  type="button"
                  onClick={() => {
                    setMessage('');
                    setError('');
                    setOtp('');
                    setOtpSent(false);
                    setOtpTimer(0);
                    clearPhoneAuthSession();
                    phoneConfirmRef.current = null;
                    if (otpIntervalRef.current) {
                      clearInterval(otpIntervalRef.current);
                    }
                    // Prefer password login when account has email
                    setCurrentStep(userExists?.email ? 'login' : 'entry');
                  }}
                  className="inline-flex items-center justify-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-gray-600 hover:text-gray-700 active:text-gray-800 font-medium transition-colors py-2 sm:py-0 touch-manipulation min-h-[44px] sm:min-h-0"
                >
                  <ArrowLeft className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  Back
                </button>
              </div>
            </form>
            </div>
          )}

          {/* Step 4: Signup */}
          {currentStep === 'signup' && (
            <form onSubmit={handleSignup} className="space-y-4 sm:space-y-5">
              <div className="text-center mb-3 sm:mb-4">
                <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-green-100 to-emerald-100 rounded-xl sm:rounded-2xl mb-2 sm:mb-3">
                  <User className="w-6 h-6 sm:w-8 sm:h-8 text-green-600" />
                </div>
                <h2 className="text-lg sm:text-xl font-bold text-gray-900">Create your account</h2>
                <p className="text-xs sm:text-sm text-gray-600 mt-1 sm:mt-2">Fill in your details to get started</p>
              </div>

              <div className="space-y-2">
                <label htmlFor="name" className="block text-xs sm:text-sm font-semibold text-gray-700 flex items-center gap-1.5 sm:gap-2">
                  <User className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-600" />
                  Full Name
                </label>
                <input
                  id="name"
                  type="text"
                  value={userDetails.name}
                  onChange={(e) => setUserDetails({...userDetails, name: e.target.value})}
                  placeholder="Enter your full name"
                  className="block w-full px-3 sm:px-4 py-3 sm:py-3.5 border-2 border-gray-200 rounded-lg sm:rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50 focus:bg-white text-sm sm:text-base touch-manipulation"
                  required
                  autoComplete="name"
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="email" className="block text-xs sm:text-sm font-semibold text-gray-700 flex items-center gap-1.5 sm:gap-2">
                  <Mail className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-600" />
                  Email Address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 sm:pl-4 flex items-center pointer-events-none">
                    <Mail className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                  </div>
                  <input
                    id="email"
                    type="email"
                    value={userDetails.email}
                    onChange={(e) => setUserDetails({...userDetails, email: e.target.value})}
                    placeholder="Enter your email"
                    className="block w-full pl-10 sm:pl-12 pr-4 py-3 sm:py-3.5 border-2 border-gray-200 rounded-lg sm:rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50 focus:bg-white text-sm sm:text-base touch-manipulation"
                    required
                    autoComplete="email"
                    inputMode="email"
                  />
                </div>
                {validateEmail(inputValue) && userDetails.email === inputValue && (
                  <p className="mt-1 text-xs text-green-600 flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" />
                    Pre-filled from your entry
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <label htmlFor="phone" className="block text-xs sm:text-sm font-semibold text-gray-700 flex items-center gap-1.5 sm:gap-2">
                  <Phone className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-600" />
                  Phone Number
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 sm:pl-4 flex items-center pointer-events-none">
                    <Phone className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                  </div>
                  <input
                    id="phone"
                    type="tel"
                    value={userDetails.phone}
                    onChange={(e) => setUserDetails({...userDetails, phone: e.target.value})}
                    placeholder="Enter your phone number"
                    className="block w-full pl-10 sm:pl-12 pr-4 py-3 sm:py-3.5 border-2 border-gray-200 rounded-lg sm:rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50 focus:bg-white text-sm sm:text-base touch-manipulation"
                    required
                    autoComplete="tel"
                    inputMode="tel"
                  />
                </div>
                {validatePhone(inputValue) && userDetails.phone === inputValue && (
                  <p className="mt-1 text-xs text-green-600 flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" />
                    Pre-filled from your entry
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <label htmlFor="password" className="block text-xs sm:text-sm font-semibold text-gray-700 flex items-center gap-1.5 sm:gap-2">
                  <Lock className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-600" />
                  Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 sm:pl-4 flex items-center pointer-events-none">
                    <Lock className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                  </div>
                  <input
                    id="password"
                    type="password"
                    value={userDetails.password}
                    onChange={(e) => setUserDetails({...userDetails, password: e.target.value})}
                    placeholder="Create a strong password"
                    className="block w-full pl-10 sm:pl-12 pr-4 py-3 sm:py-3.5 border-2 border-gray-200 rounded-lg sm:rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50 focus:bg-white text-sm sm:text-base touch-manipulation"
                    required
                    autoComplete="new-password"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600 text-white py-3.5 sm:py-4 px-4 rounded-lg sm:rounded-xl hover:from-green-700 hover:via-emerald-700 hover:to-teal-700 active:scale-95 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 shadow-lg hover:shadow-xl transition-all duration-200 sm:hover:scale-[1.02] flex items-center justify-center gap-2 text-sm sm:text-base font-semibold min-h-[44px] touch-manipulation"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Creating...</span>
                  </>
                ) : (
                  <>
                    <User className="w-4 h-4 sm:w-5 sm:h-5" />
                    <span>Create Account</span>
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={() => {
                  setCurrentStep('entry');
                  setMessage('');
                  setError('');
                  setUserDetails({ name: '', email: '', phone: '', password: '' });
                }}
                className="w-full inline-flex items-center justify-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-gray-600 hover:text-gray-700 active:text-gray-800 font-medium transition-colors py-2 sm:py-0 touch-manipulation min-h-[44px] sm:min-h-0"
              >
                <ArrowLeft className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                Back to entry
              </button>
            </form>
          )}

          {/* Step 5: Password Reset */}
          {currentStep === 'reset' && (
            <div className="space-y-4 sm:space-y-5">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-orange-100 to-red-100 rounded-xl sm:rounded-2xl mb-2 sm:mb-3">
                  <Lock className="w-6 h-6 sm:w-8 sm:h-8 text-orange-600" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900">Reset Password</h3>
                <p className="mt-1 sm:mt-2 text-xs sm:text-sm text-gray-600 flex items-center justify-center gap-1.5 sm:gap-2 px-2">
                  <Mail className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  <span className="break-all">
                    {userExists?.email
                      ? `We'll send a password reset link to ${userExists.email}`
                      : 'We will send a password reset link to your account email.'}
                  </span>
                </p>
              </div>

              <button
                onClick={handlePasswordReset}
                disabled={loading}
                className="w-full bg-gradient-to-r from-orange-600 via-red-600 to-pink-600 text-white py-3.5 sm:py-4 px-4 rounded-lg sm:rounded-xl hover:from-orange-700 hover:via-red-700 hover:to-pink-700 active:scale-95 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 shadow-lg hover:shadow-xl transition-all duration-200 sm:hover:scale-[1.02] flex items-center justify-center gap-2 text-sm sm:text-base font-semibold min-h-[44px] touch-manipulation"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 sm:w-5 sm:h-5" />
                    <span>Send Reset Link</span>
                  </>
                )}
              </button>
              
              <button
                onClick={() => {
                  setCurrentStep(userExists?.email ? 'login' : 'entry');
                  setMessage('');
                  setError('');
                }}
                className="w-full inline-flex items-center justify-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-gray-600 hover:text-gray-700 active:text-gray-800 font-medium transition-colors py-2 sm:py-0 touch-manipulation min-h-[44px] sm:min-h-0"
              >
                <ArrowLeft className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                Back to Login
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewLogin;
